/**
 * Guards for the dev-only device-token sign-in (`app/dev/sign-in.tsx`). Each lives here rather
 * than inline so the route and `authService` apply the SAME rules, and a spec can drive them.
 */

/** True only in a development build. A release build compiles `__DEV__` to `false`. */
export const isDevBuild = (): boolean => typeof __DEV__ !== 'undefined' && __DEV__;

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/** True when `url` points at this machine — a token must never be replayed at a shared API. */
export const isLocalApiUrl = (url: string | null | undefined): boolean => {
	if (!url) return false;
	try {
		return LOCAL_HOSTS.has(new URL(url).hostname);
	} catch {
		return false;
	}
};

/** The API's own device-token shape (api/index.ts): optional `PREFIX-`, then hex; at least 64 chars. */
export const isDeviceTokenShape = (token: unknown): token is string =>
	typeof token === 'string' && token.length >= 64 && /^([A-Z]+-)?[0-9a-f]+$/i.test(token);

export const DEV_SIGN_IN_LOCAL_ONLY = 'Dev sign-in is only available against a local API';
export const DEV_SIGN_IN_BAD_TOKEN = 'That is not a valid device token.';

/**
 * Why a dev sign-in must NOT proceed, or `null` when it may. The route and the warm-link
 * listener both ask this, so the guards cannot drift apart. The dev-build check comes first
 * and is separate: a release build should show nothing at all, not a reason.
 */
export const devSignInProblem = (token: unknown, apiBaseUrl: string | null | undefined): string | null => {
	if (!isLocalApiUrl(apiBaseUrl)) return DEV_SIGN_IN_LOCAL_ONLY;
	if (!isDeviceTokenShape(token)) return DEV_SIGN_IN_BAD_TOKEN;
	return null;
};
